import S from 's-js';
import * as Promise from 'bluebird';

export enum PollsterStartupBehavior {
    /**
     * The `Pollster` should send another request as soon as possible, even if the pollster is already polling
     * when `start` is called.
     */
    Eager,
    /**
     * If the `Pollster` is already polling when `start` is called, then it should continue on its regular
     * schedule, otherwise it should fire a request immediately and continue polling after that. This is
     * equivalent to `Lazy` when the `Pollster` is already polling and `Eager` when it isn't.
     */
    Default,
    /**
     * If the `Pollster` is already polling when `start` is called, then it should continue on its regular
     * schedule, otherwise it should (re)start polling now but shouldn't fire its next request until after its
     * delay has passed.
     */
    Lazy
}

/**
 * Pollster class
 *
 * A utility to call a request function repeatedly on a delay, with the guarantee that it will always wait for
 * the current request to finish before sending the next request.
 */
export class Pollster {
    // Semantically, S.value would make sense here, but using S.data instead to work around
    // https://github.com/adamhaile/S/issues/25. Our issue is that 1) the only time we ever set _isRequesting
    // to false is in a Promise.finally handler, meaning no S clock is running at that point, 2) it's common
    // for this _isRequesting signal to have no listeners, b/c most Pollster use cases don't need it, and 3)
    // that combo of updating an S.value that has no listeners at a time when no S clock is running is
    // precisely the situation that triggers https://github.com/adamhaile/S/issues/25. This was leading to
    // polling stopping unexpectedly on the Dispatch Dashboard due to S throwing a "conflicting values" error
    // when we try to set _isRequesting to true at the start of the next fireRequest call.
    private readonly _isRequesting = S.data(false);
    /**
     * Does the `Pollster` have a request in flight right now?
     */
    public readonly isRequesting : () => boolean;

    private timeoutId : number | undefined;
    private _isPolling = false;
    private fireEagerly = false;

    /**
     * Construct a new `Pollster` instance. The `Pollster` will initially be in a stopped state (not polling) but
     * can be started with a call to `start`.
     * @param reqFn Function to issue a single request.
     * @param delay Delay between polls in millseconds.
     */
    constructor(
        private readonly reqFn : () => PromiseLike<unknown>,
        private readonly delay : number
    ) {
        this.isRequesting = () => this._isRequesting();
        S.cleanup(() => this.stop());
    }

    /**
     * Is the `Pollster` polling in general. I.e. `false` if the `Pollster` is in a stopped state and `true`
     * otherwise.
     */
     get isPolling() { return this._isPolling; }

     /**
     * Start or restart polling. Calling this method on an already-started `Pollster` has no effect unless the
     * provided `behavior` is `PollsterStartupBehavior.Eager`.
     */
    start(behavior = PollsterStartupBehavior.Default) {
        if (behavior === PollsterStartupBehavior.Eager || !this.isPolling) {
            this.stop();
            this._isPolling = true;
            if (S.sample(this.isRequesting)) {
                this.fireEagerly = behavior === PollsterStartupBehavior.Eager;
            } else {
                if (behavior === PollsterStartupBehavior.Lazy) this.setTimeout();
                else this.fireRequest();
            }
        }
        return this;
    }
    
    private fireRequest() {
        this.fireEagerly = false;
        this._isRequesting(true);
        Promise.resolve(this.reqFn()).finally(() => {
            if (this.isPolling) {
                if (this.fireEagerly) {
                    this.fireRequest();
                } else {
                    this.setTimeout();
                    this._isRequesting(false);
                }
            } else {
                this._isRequesting(false);
            }
        });
    }

    private setTimeout() {
        this.timeoutId = window.setTimeout(() => this.fireRequest(), this.delay);
    }

    /**
     * Stop polling. Polling can later be restarted by calling `start`. Calling this method on a
     * already-stopped `Pollster` has no effect.
     */
    stop() {
        this._isPolling = false;
        window.clearTimeout(this.timeoutId);
    }
}



// WEBPACK FOOTER //
// ./lib/pollster.ts